// # EmptyState · Skeleton · ScrollArea · Kbd — 비었을 때·기다릴 때·넘칠 때·키를 적을 때(docs/design.md)
//
// - `EmptyState` 는 "없다" 를 말하되 **다음 할 일**을 같이 준다(`action`). 빈 판에 글자 한 줄만
//   남기면 사람은 고장인지 정말 없는 것인지 모른다. 아이콘은 꾸밈이라 읽히지 않는다.
// - `Skeleton` 은 읽히지 않는다(`aria-hidden`). 기다린다는 사실은 감싼 자리가 `aria-busy` 나
//   `SkeletonText` 의 `label`(`role="status"`) 로 한 번만 말한다 — 회색 막대 열 개를 열 번 읽으면 소음이다.
//   반짝임은 움직임을 줄여 달라면 base 층이 멈춘다.
// - `ScrollArea` 는 넘치는 판 하나다. 얇은 막대(`scrollbar-width: thin`)로 그리고, 이름(`label`)이
//   있으면 `role="region"` + `tabIndex=0` 으로 키보드가 들어가 화살표로 굴릴 수 있다(axe
//   scrollable-region-focusable). 막대를 가짜로 그리지 않는다 — 운영체제의 관성·접근성을 잃는다.
// - `Spinner` 는 도는 표시 하나다. 기본은 꾸밈(`aria-hidden`) — 곁에 "불러오는 중" 글자나 `role="status"` 가
//   이미 있는 자리가 대부분이라 두 번 읽히면 소음이다. 혼자 서는 자리만 `label` 을 주면 `role="status"` 와
//   숨긴 글자로 한 번 읽힌다(G10). 움직임을 줄여 달라면 base 층이 한 바퀴로 멈춘다.
// - `Kbd` 는 키 하나 또는 조합이다. 조합은 HTML 규약대로 `<kbd>` 안에 `<kbd>` 를 넣는다.
//
// 너비·높이는 인라인 style 이 아니라 `data-*` 로 고른다 — 화면에 인라인 style 을 남기지 않는다.

import { LoaderCircle } from 'lucide-react'
import type { HTMLAttributes, ReactNode, Ref } from 'react'
import { useT } from '../i18n.ts'
import { cx } from './cx.ts'

export interface EmptyStateProps {
  /** 꾸밈 아이콘(lucide 요소). 읽히지 않는다. */
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  /** 다음 할 일 — 단추 하나나 둘. */
  action?: ReactNode
  /** 기본 `md`. 옆 판·팝오버 안에서는 `sm`. */
  size?: 'sm' | 'md'
  /** 제목 단계. 기본 3. 설정 묶음(h4) 안에서는 5. */
  level?: 2 | 3 | 4 | 5
  className?: string
}

export function EmptyState(props: EmptyStateProps) {
  const Heading = `h${props.level ?? 3}` as 'h3'
  return (
    <div className={cx('EmptyState', props.className)} data-size={props.size ?? 'md'}>
      {props.icon && (
        <span className="EmptyState-icon" aria-hidden>
          {props.icon}
        </span>
      )}
      <Heading className="EmptyState-title">{props.title}</Heading>
      {props.description && <p className="EmptyState-description">{props.description}</p>}
      {props.action && <div className="EmptyState-action">{props.action}</div>}
    </div>
  )
}

export type SkeletonShape = 'line' | 'circle' | 'block'

export interface SkeletonProps {
  /** 기본 `line`(글자 한 줄 높이). `circle` 은 아바타, `block` 은 그림·카드. */
  shape?: SkeletonShape
  /** 줄 너비. 기본 `full`. */
  width?: 'full' | '3/4' | '1/2' | '1/3'
  /** `circle` 의 지름 — `Avatar` 크기와 같은 이름. 기본 `md`. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

export function Skeleton(props: SkeletonProps) {
  const shape = props.shape ?? 'line'
  return (
    <span
      aria-hidden
      className={cx('Skeleton', props.className)}
      data-shape={shape}
      data-width={shape === 'line' ? (props.width ?? 'full') : undefined}
      data-size={shape === 'circle' ? (props.size ?? 'md') : undefined}
    />
  )
}

export interface SkeletonTextProps {
  /** 줄 수. 기본 3. 마지막 줄은 짧다 — 문단처럼 보이게. */
  lines?: number
  /** 기다림을 알릴 이름. 기본 "불러오는 중". */
  label?: string
  className?: string
}

/** 문단 모양 자리표시. `role="status"` 로 한 번만 읽힌다. */
export function SkeletonText(props: SkeletonTextProps) {
  const t = useT()
  const n = Math.max(1, props.lines ?? 3)
  return (
    <div
      role="status"
      aria-label={props.label ?? t('불러오는 중')}
      className={cx('SkeletonText', props.className)}
    >
      {Array.from({ length: n }, (_, i) => (
        <Skeleton key={i} width={i === n - 1 && n > 1 ? '1/2' : 'full'} />
      ))}
    </div>
  )
}

export interface ScrollAreaProps extends HTMLAttributes<HTMLDivElement> {
  /** 이름이 있으면 키보드로 들어가 굴릴 수 있는 영역(`role="region"`)이 된다. */
  label?: string
  /** 기본 `vertical`. */
  axis?: 'vertical' | 'horizontal' | 'both'
  ref?: Ref<HTMLDivElement>
}

export function ScrollArea(props: ScrollAreaProps) {
  const { label, axis, className, ...rest } = props
  const reach = label ? { role: 'region', 'aria-label': label, tabIndex: 0 } : {}
  return (
    <div
      {...rest}
      {...reach}
      className={cx('ScrollArea', className)}
      data-axis={axis ?? 'vertical'}
    />
  )
}

export interface KbdProps {
  /** 키 하나(`"Esc"`) 또는 조합(`["⌘", "K"]`). */
  keys: string | readonly string[]
  /** 기본 `md`. 풍선·메뉴 안에서는 `sm`. */
  size?: 'sm' | 'md'
  className?: string
}

export function Kbd(props: KbdProps) {
  const size = props.size ?? 'md'
  if (typeof props.keys === 'string') {
    return (
      <kbd className={cx('Kbd', props.className)} data-size={size}>
        {props.keys}
      </kbd>
    )
  }
  return (
    <kbd className={cx('Kbd-combo', props.className)} data-size={size}>
      {props.keys.map((k, i) => (
        <kbd key={i} className="Kbd" data-size={size}>
          {k}
        </kbd>
      ))}
    </kbd>
  )
}

/** 도는 표시의 크기(px). 글자 곁(`xs`·`sm`), 단추 안(`md`), 화면 가운데(`lg`). */
export const SPINNER_SIZE = { xs: 12, sm: 14, md: 16, lg: 24 } as const

export interface SpinnerProps {
  /** 기본 `sm`. */
  size?: keyof typeof SPINNER_SIZE
  /** 혼자 서는 자리의 이름("불러오는 중"). 주면 `role="status"` 로 한 번 읽힌다. 없으면 꾸밈이다. */
  label?: string
  className?: string
}

export function Spinner(props: SpinnerProps) {
  const icon = (
    <LoaderCircle
      className={cx('Spinner', 'spin', props.label === undefined && props.className)}
      size={SPINNER_SIZE[props.size ?? 'sm']}
      aria-hidden
    />
  )
  if (props.label === undefined) return icon
  return (
    <span className={cx('Spinner-wrap', props.className)} role="status">
      {icon}
      <span className="sr-only">{props.label}</span>
    </span>
  )
}

export interface ProgressBarProps {
  value: number
  max: number
  /** 읽을 이름("시작하기 3/5"). 곁에 이미 수가 적혀 있으면 `decorative` 로 감춘다. */
  label?: string
  decorative?: boolean
  /** 기본 강조색. 다 된 일은 `ok`. */
  tone?: 'accent' | 'ok'
  className?: string
}

/** 얼마나 됐는지의 막대(G17). 네이티브 `<progress>` 라 역할·값이 저절로 읽힌다. */
export function ProgressBar(props: ProgressBarProps) {
  return (
    <progress
      className={cx('ProgressBar', props.className)}
      max={props.max}
      value={props.value}
      data-tone={props.tone ?? 'accent'}
      aria-label={props.decorative === true ? undefined : props.label}
      aria-hidden={props.decorative === true || undefined}
    />
  )
}
